import { useEffect, useRef, useState } from 'react';
import { Job, JobType, Status } from '../apis/dtos';
import {
  getExternalJob,
  getMockedShipmentDetails,
  getShipmentDetails,
  getMockedShipmentDetailsWithContainers,
  getMockedShipmentDetailsWithDeliveredContainers,
} from '../apis/shipments';
import { useAnalyticsTrack } from '@chr/react-analytics';
import { addIdToObjects } from '../utilities/addIdToObjects';
import { JobWithAddedIds } from '../internalModels';
import { throwShipmentError } from './shipmentError';
import { formatTime, formatDate } from '../utilities/display-utilities';
import { useChrLdFlag } from '@chr/common-launchdarkly';

type Nullable<T> = T | null;

export interface UseShipmentResult {
  shipment: JobWithAddedIds;
  isLoading: boolean;
  fetchShipmentDetails: (id: string) => Promise<void>;
}

export const useShipment = (chrTrackingNumber: string, referenceNumber?: string, postalCode?: string, captchaToken?: string): UseShipmentResult => {
  const [shipment, setShipment] = useState<JobWithAddedIds>(
    {} as JobWithAddedIds
  );
  const [isLoading, setIsLoading] = useState(!!chrTrackingNumber || (!!referenceNumber && !!postalCode));
  const enhancedSearch = useChrLdFlag('enhanced-externaltracking-search', false);
  const track = useAnalyticsTrack();
  // Captcha tokens (Cloudflare Turnstile) are single-use. Track the last token
  // that was sent to the API so we never replay it (which causes 409 from the
  // gateway). React StrictMode double-invocation, parent re-renders, or retries
  // would otherwise reuse the same already-consumed token.
  const consumedCaptchaTokenRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    if (enhancedSearch && !captchaToken) {
      setIsLoading(false);
      return;
    }

    if (captchaToken && consumedCaptchaTokenRef.current === captchaToken) {
      // Token has already been used for a request; require a fresh challenge.
      setIsLoading(false);
      return;
    }

    const doFetch = async () => {
      if (chrTrackingNumber) {
        setIsLoading(true);
        setShipment({} as JobWithAddedIds);
        if (captchaToken) {
          consumedCaptchaTokenRef.current = captchaToken;
        }
        try {
          const data = await fetchShipmentDetailData(chrTrackingNumber.trim());
          if (!cancelled) {
            processShipmentData(data, chrTrackingNumber.trim());
          }
        } catch (error) {
          if (!cancelled) throwShipmentError(error);
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      } else if (enhancedSearch && referenceNumber && postalCode) {
        setIsLoading(true);
        setShipment({} as JobWithAddedIds);
        if (captchaToken) {
          consumedCaptchaTokenRef.current = captchaToken;
        }
        try {
          const data = await getExternalJob(referenceNumber.trim(), postalCode.trim(), captchaToken);
          if (!cancelled) {
            processShipmentData(data, referenceNumber.trim());
          }
        } catch (error) {
          if (!cancelled) throwShipmentError(error);
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
  }, [enhancedSearch, chrTrackingNumber, referenceNumber, postalCode, captchaToken]);

  const processShipmentData = (data: Nullable<Job>, searchIdentifier: string) => {
    if (data && data.chrTrackingNumber) {
      setShipment({
        ...data,
        trackingEvents: addIdToObjects(data.trackingEvents || []),
        items: addIdToObjects(data.items || []),
      });

      let deliveryDate: string | undefined = undefined;

      if (data?.status === Status.Delivered) {
        const sortedStops = [...(data.stops || [])].sort((a, b) => b.sequenceNumber - a.sequenceNumber);
        const lastCompletedStop = sortedStops.find(stop => stop.arrived ?? stop.departed);
        const deliveryDateTime = lastCompletedStop?.arrived ?? lastCompletedStop?.departed;
        if (deliveryDateTime) {
          const date = formatDate(deliveryDateTime);
          const time = formatTime(deliveryDateTime);
          deliveryDate = `${date} ${time}`;
        }
      }

      track({
        event: 'externalTrackingSearchResult',
        referenceNumberLabel: data.matchedField,
        trackingNumber: postalCode ? undefined : data.chrTrackingNumber,
        referenceNumber: referenceNumber,
        postalCode: postalCode,
        mode: data.mode,
        riskLevel: data.riskLevel,
        status: data.status,
        jobType: jobTypeToString(data.jobType),
        division: data.division,
        source: postalCode ? 'referenceNumberSearch' : 'trackingNumberSearch',
        deliveryDate: deliveryDate,
        site: {
          name: 'Online.ExternalTracking.UI',
          language: navigator.language,
        },
      });
    } else {
      setShipment({} as JobWithAddedIds);
      track({
        event: 'externalTrackingNotFound',
        referenceNumberLabel: undefined,
        trackingNumber: postalCode ? undefined : searchIdentifier,
        referenceNumber: referenceNumber,
        postalCode: postalCode,
        source: postalCode ? 'referenceNumberSearch' : 'trackingNumberSearch',
        jobType: 'ResultNotFound',
        division: undefined,
        site: {
          name: 'Online.ExternalTracking.UI',
          language: navigator.language,
        },
      });
    }
  };

  const fetchShipmentDetailData = async (id: string): Promise<Nullable<Job>> => {
    switch (id) {
      case 'mocked':
      case 'mockedAir':
        return getMockedShipmentDetails(id);

      case '465':
      case 'mockedContainers':
        return getMockedShipmentDetailsWithContainers(id);

      case 'mockedManyContainers':
        return getMockedShipmentDetailsWithContainers(id, true);

      case 'mockedManyContainersSingleStop':
        return getMockedShipmentDetailsWithContainers(id, true, true);

      case 'mockedManyContainersAllDelivered':
        return getMockedShipmentDetailsWithDeliveredContainers(id);

      case 'not-found':
        return null;

      default:
        return getShipmentDetails(id, captchaToken);
    }
  };

  const fetchShipmentDetails = async (id: string) => {
    if (captchaToken && consumedCaptchaTokenRef.current === captchaToken) {
      // Refuse to replay an already-consumed single-use captcha token.
      return;
    }
    setIsLoading(true);
    setShipment({} as JobWithAddedIds);
    if (captchaToken) {
      consumedCaptchaTokenRef.current = captchaToken;
    }
    try {
      const data = await fetchShipmentDetailData(id);
      processShipmentData(data, id);
    } catch (error) {
      throwShipmentError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    shipment,
    isLoading,
    fetchShipmentDetails,
  } as const;
};

function jobTypeToString(jobType: JobType | undefined): string {
  switch (jobType) {
    case JobType.Order:
      return 'Order';
    case JobType.Shipment:
      return 'Shipment';
    default:
      return 'Unknown';
  }
}
